import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiService } from '../services/api';
import AchievementUnlockedModal from '../components/achievement/AchievementUnlockedModal';
import type { AchievementPeekItem, AchievementSurface } from '../types/achievement';

/**
 * 현재 경로를 백엔드 [AchievementSurface] 로 매핑한다.
 * peek 요청의 URI 로는 surface 를 판별할 수 없으므로(인터셉터 규칙 미매칭), 프론트가 직접 넘겨준다.
 * 도전과제는 v7 전용이라 `/7`(및 `/7/legacy`) 프리픽스만 처리한다.
 */
function currentSurface(): AchievementSurface | undefined {
  const match = window.location.pathname.match(/^\/7(?:\/legacy)?(\/.*)?$/);
  if (!match) return undefined;
  const sub = (match[1] ?? '').replace(/\/+$/, '');
  if (sub === '' || sub.startsWith('/dashboard')) return 'DASHBOARD'; // `/7` → dashboard redirect
  if (sub.startsWith('/record')) return 'RECORD';
  if (sub.startsWith('/rival') || sub.startsWith('/duel')) return 'RIVAL';
  if (sub.startsWith('/bingo')) {
    return /^\/bingo\/(?!create|edit)[^/]+/.test(sub) ? 'BINGO_DETAIL' : 'BINGO_LIST';
  }
  if (sub.startsWith('/songs') || sub.startsWith('/song/')) return 'SEARCH_RESULT';
  if (sub.includes('/export')) return 'EXPORT_RESULT';
  return undefined;
}

interface AchievementToastContextType {
  /** Manually enqueue one or more achievement items for display. */
  enqueue: (items: AchievementPeekItem[]) => void;
}

const AchievementToastContext = createContext<AchievementToastContextType | undefined>(undefined);

export function useAchievementToast(): AchievementToastContextType {
  const ctx = useContext(AchievementToastContext);
  if (!ctx) {
    throw new Error('useAchievementToast must be used within AchievementToastProvider');
  }
  return ctx;
}

interface Props {
  children: ReactNode;
}

export function AchievementToastProvider({ children }: Props) {
  const [queue, setQueue] = useState<AchievementPeekItem[]>([]);
  // Prevent concurrent peek calls when multiple events fire rapidly
  const fetchingRef = useRef(false);

  const enqueue = useCallback((items: AchievementPeekItem[]) => {
    if (items.length === 0) return;
    setQueue((prev) => [...prev, ...items]);
  }, []);

  const handlePeek = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const items = await apiService.peekAchievements(currentSurface());
      enqueue(items);
    } catch {
      // Network errors during peek are silently ignored — non-critical
    } finally {
      fetchingRef.current = false;
    }
  }, [enqueue]);

  useEffect(() => {
    const handler = () => {
      void handlePeek();
    };
    window.addEventListener('achievement:pending', handler);
    return () => window.removeEventListener('achievement:pending', handler);
  }, [handlePeek]);

  const handleClose = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  const currentItem = queue[0] ?? null;

  return (
    <AchievementToastContext.Provider value={{ enqueue }}>
      {children}
      {currentItem && (
        <AchievementUnlockedModal
          key={currentItem.achievementId}
          item={currentItem}
          onClose={handleClose}
        />
      )}
    </AchievementToastContext.Provider>
  );
}
